<template>
  <div>
    <trade-contract-info
        v-if="!hideTradeContractInfo"
        :trade-contract="tradeContract"
        :companion="tradeContract.isPublishMode && tradeContract.amiIssuer ? null : tradeContract.companions"
        readonly>
      <template #append>
        <v-row dense>
          <v-col cols="3">
            <text-field-like
              label="開始日時"
              placeholder="作成日"
              :value="formatDateTime(tradeContract.startDatetime)">
              <template #prepend>
                <v-icon>mdi-calendar-month</v-icon>
              </template>
            </text-field-like>
          </v-col>
          <v-col cols="3">
            <text-field-like
                label="終了日時"
                placeholder="自動更新"
                :value="formatDateTime(tradeContract.endDatetime)">
              <template #prepend>
                <v-icon>mdi-calendar-month</v-icon>
              </template>
            </text-field-like>
          </v-col>
          <v-col cols="3">
            <text-field-like
                label="作成日時"
                :value="formatDateTime(tradeContract.createdAt)">
            </text-field-like>
          </v-col>
        </v-row>
      </template>
    </trade-contract-info>

    <v-alert
        v-if="expireDateDescription"
        class="mb5"
        icon="mdi-information-outline"
        outlined>
      {{ expireDateDescription }}
    </v-alert>

    <template v-for="(tc, index) in tradeContracts">
      <div :key="tc.id">
        <h3
            :class="{
              'trade-contract-header': true,
              'first-trade-contract-header': index === 0,
            }"
        >
          <span class="index">{{ index + 1 }} / {{ tradeContracts.length }}</span>
          <span class="id">{{ tc.id }}</span>
        </h3>
        <trade-contract-view-sole-panel
            :trade-contract="tc"
            :activate="activate"
            :hide-self-data-profile="hideSelfDataProfile"
            :hide-self-service-profile="hideSelfServiceProfile"
            :hide-other-data-profile="hideOtherDataProfile"
            :hide-other-service-profile="hideOtherServiceProfile"
            :hide-other-event-profile="hideOtherEventProfile"
        >
          <template #self-data-profile-title>
            <slot name="self-data-profile-title"></slot>
          </template>
          <template #self-service-profile-title>
            <slot name="self-service-profile-title"></slot>
          </template>
          <template #other-data-profile-title>
            <slot name="other-data-profile-title"></slot>
          </template>
          <template #other-service-profile-title>
            <slot name="other-service-profile-title"></slot>
          </template>

          <template #after-self-data-profile="values">
            <slot v-bind="values" name="after-self-data-profile"></slot>
          </template>
          <template #after-self-service-profile="values">
            <slot v-bind="values" name="after-self-service-profile"></slot>
          </template>
          <template #after-other-data-profile="values">
            <slot v-bind="values" name="after-other-data-profile"></slot>
          </template>
          <template #after-other-service-profile="values">
            <slot v-bind="values" name="after-other-service-profile"></slot>
          </template>
        </trade-contract-view-sole-panel>
      </div>
    </template>

    <!-- メッセージ履歴 -->
    <message-history
        v-if="!hideMessages"
        :trade-contract="tradeContract">
    </message-history>
  </div>
</template>

<script>
import {emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import TradeContractInfo from "@/components/organisms/TradeContractInfo.vue";
import {mapGetters} from "vuex";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import Formatter from "@/utils/Formatter";
import MessageHistory from "@/components/pages/contract/TradeContract/MessageHistory.vue";
import {DateTime} from "luxon";
import {isNullish} from "@/utils/TypeUtil";
import TradeContractViewSolePanel from "@/components/pages/contract/TradeContract/TradeContractViewSolePanel.vue";

export default {
  name: "TradeContractViewPanel",
  components: {
    TradeContractViewSolePanel,
    MessageHistory,
    TextFieldLike,
    TradeContractInfo},
  mixins: [Formatter],
  props: {
    tradeContracts: {
      type: Array /* as PropType<TradeContractBaseView[]> */,
      required: true
    },
    activate: {
      type: Boolean,
      required: true
    },
    hideTradeContractInfo: {
      type: Boolean,
      default: false
    },
    hideSelfDataProfile: {
      type: Boolean,
      default: false
    },
    hideSelfServiceProfile: {
      type: Boolean,
      default: false
    },
    hideOtherDataProfile: {
      type: Boolean,
      default: false
    },
    hideOtherServiceProfile: {
      type: Boolean,
      default: false
    },
    hideOtherEventProfile: {
      type: Boolean,
      default: false
    },
    hideMessages: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      now: DateTime.now(),
    };
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    tradeContract() {
      return this.tradeContracts[0] ?? emptyTradeContractView();
    },
    expireDateDescription() {
      const tradeContract = this.tradeContract;
      const expireDate = tradeContract.expireDate;
      if(isNullish(expireDate)) {
        return '';
      }
      const date = this.formatDate(expireDate);
      const rest = Math.round(expireDate.diff(this.now).as('days'));

      if(tradeContract.isPublishMode) { // 募集モード
        if(rest >= 0) {
          return `本契約は ${date} (残り${rest}日) で募集は終了します。`;
        } else {
          return `本契約は ${date} をもちまして募集が終了しました。`;
        }
      } else if(tradeContract.isSubscribeMode) { // 申込モード
        if(tradeContract.status === 'applying') {
          if (tradeContract.amiIssuer) {
            return `本契約は ${date} (残り${rest}日) までに取引先から回答がない場合、自動的に不成約となります。`;
          } else {
            return `本契約は ${date} (残り${rest}日) までにこちらが回答しない場合、自動的にシステムにより不成約となります。`;
          }
        }
      } else if(tradeContract.isRequestMode) { // 申請モード
        if(tradeContract.status === 'requesting') {
          if (tradeContract.amiIssuer) {
            return `本契約は ${date} (残り${rest}日) までに取引先から回答がない場合、自動的に不成約となります。`;
          } else {
            return `本契約は ${date} (残り${rest}日) までにこちらが回答しない場合、自動的にシステムにより不成約となります。`;
          }
        }
      }
      return null;
    }
  },
}
</script>

<style lang="scss" scoped>

h3.trade-contract-header {
  text-align: left;
  &:not(.first-trade-contract-header) {
    margin-top: 2rem;
  }

  & .index {
    user-select: none;
  }

  & .id {
    margin-left: 10px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 70%;
    font-weight: normal;
    vertical-align: text-bottom;
  }
}

.v-alert {
  border-color: rgba(0,0,0,0.12) !important;

  ::v-deep .v-alert__content {
    text-align: left;
  }
}

</style>
